import React, { useState } from 'react';
import { FiX, FiClock, FiAlertCircle } from 'react-icons/fi';
import { userRequest } from '../utils/requestMethods';
import { toast } from 'react-toastify';

const ExtendTestSessionModal = ({ isVisible, onClose, student, testDetails, testId }) => {
  const [duration, setDuration] = useState(30);
  const [extending, setExtending] = useState(false);
  const [error, setError] = useState('');

  if (!isVisible) return null;

  const handleExtend = async () => {
    if (!duration || duration <= 0) {
      setError('Please enter a valid duration');
      return;
    }

    setExtending(true);
    setError('');
    
    try {
      await userRequest.post('/test-session/extend', {
        testId: testId,
        childId: student.child._id,
        duration: duration
      });
      
      toast.success(`Test session extended by ${duration} minutes successfully!`);
      onClose();
    } catch (error) {
      console.error('Error extending test session:', error);
      setError('Failed to extend test session. Please try again.');
      toast.error('Failed to extend test session');
    } finally {
      setExtending(false);
    }
  };

  const handleDurationChange = (e) => {
    const value = parseInt(e.target.value);
    setDuration(value);
    setError('');
  };

  const getCurrentStatus = () => {
    if (student.status === 'completed') {
      return { text: 'Completed', color: 'text-green-600', bg: 'bg-green-50' };
    } else if (student.status === 'in_progress') {
      return { text: 'In Progress', color: 'text-yellow-600', bg: 'bg-yellow-50' };
    } else {
      return { text: 'Not Started', color: 'text-gray-600', bg: 'bg-gray-50' };
    }
  };

  const status = getCurrentStatus();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        {/* Header */}
        <div className="flex items-center mb-6">
          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
            <FiClock className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Extend Test Session</h3>
            <p className="text-sm text-slate-500">Add extra time for this student</p>
          </div>
        </div>

        {/* Student Info */}
        <div className="mb-6 p-4 bg-slate-50 rounded-lg">
          <div className="text-sm text-slate-600 mb-2">Student Information:</div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-600">Name:</span>
              <span className="font-semibold text-slate-800">
                {student.child.firstname} {student.child.lastname}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Test:</span>
              <span className="font-semibold text-slate-800">{testDetails?.title}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Status:</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                {status.text}
              </span>
            </div>
            {student.start_time && (
              <div className="flex justify-between">
                <span className="text-slate-600">Started:</span>
                <span className="text-sm text-slate-700">
                  {new Date(student.start_time).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Duration Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Additional Duration (minutes)
          </label>
          <div className="relative">
            <input
              type="number"
              min="1"
              max="120"
              value={duration}
              onChange={handleDurationChange}
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-lg font-semibold"
              placeholder="Enter minutes"
            />
            <div className="absolute right-3 top-3 text-slate-500 font-medium">
              min
            </div>
          </div>
          <div className="mt-2 text-xs text-slate-500">
            Recommended: 15-60 minutes. Maximum: 120 minutes
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <FiAlertCircle className="w-4 h-4 text-red-600 mr-2" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        {/* Warning for completed tests 
        {student.status === 'completed' && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center">
            <FiAlertCircle className="w-4 h-4 text-yellow-600 mr-2" />
            <span className="text-sm text-yellow-700">
              This test is already completed. Extending the session may not have any effect.
            </span>
          </div>
        )}*/}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={extending}
            className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleExtend}
            disabled={extending || !duration || duration <= 0}
            className="px-6 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white font-medium rounded-lg transition-colors flex items-center"
          >
            {extending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Extending...
              </>
            ) : (
              <>
                <FiClock className="w-4 h-4 mr-2" />
                Extend Session
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExtendTestSessionModal;
